import { useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Button } from './ui';

export interface Uploaded {
    path: string;
    originalName: string;
    contentType: string;
    sizeBytes: number;
}

/**
 * Picks a file, sends it, and hands back the stored path.
 *
 * It deliberately does not save anything itself. The caller puts the returned path on whatever record
 * it is editing and saves that record as usual, so a file chosen and then abandoned leaves an orphan
 * in storage rather than a half-written candidate or a logo nobody chose.
 *
 * The `accept` attribute is a convenience for the file picker, not a check — the server identifies
 * every upload by its leading bytes and refuses anything that is not what it claims.
 */
export function FileUpload({ purpose, accept, currentName, onUploaded, onCleared }: {
    purpose: 'branding' | 'question-image' | 'candidate-document';
    accept: string;
    /** The name of whatever is already attached, so the person can see what they are replacing. */
    currentName?: string | null;
    onUploaded: (file: Uploaded) => void;
    onCleared?: () => void;
}) {
    const input = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const choose = async (file: File | undefined) => {
        if (!file) return;

        setBusy(true);
        setError(null);

        try {
            onUploaded(await api.upload<Uploaded>(`/api/admin/uploads/${purpose}`, file));
        } catch (caught) {
            // The server's message names the real problem — wrong format, too large — so it is shown
            // as written rather than replaced with something vaguer.
            setError((caught as ApiError).message);
        } finally {
            setBusy(false);
            // Cleared so choosing the same file again after a failure still fires a change event.
            if (input.current) input.current.value = '';
        }
    };

    return (
        <div className="space-y-2">
            <input
                ref={input}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => void choose(e.target.files?.[0])}
            />

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="secondary"
                    busy={busy}
                    onClick={() => input.current?.click()}
                >
                    <i className="bi bi-upload" aria-hidden="true" />
                    {currentName ? 'Replace file' : 'Choose a file'}
                </Button>

                {currentName && (
                    <>
                        <span className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                            {currentName}
                        </span>
                        {onCleared && (
                            <Button type="button" variant="danger-quiet" onClick={onCleared}>
                                Remove
                            </Button>
                        )}
                    </>
                )}
            </div>

            {error && (
                <p role="alert" className="text-xs font-medium text-red-600">{error}</p>
            )}
        </div>
    );
}
